import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import {
  MAT_DIALOG_DATA, MatDialog,
  MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { of } from 'rxjs';
import {
  catchError, debounceTime, finalize, map, switchMap, take, takeUntil
} from 'rxjs/operators';

import {
  CreateFollowUpGQL, EntityCoreFragment, EntityWithBasicInfoFragment, SearchUserQueryGQL
} from 'generated/graphql';

import { selectCurrentUser } from 'app/_state/auth/auth-selectors';
import { selectCampaignId } from 'app/_state/router/router-selectors';
import { MAT_DATE_FNS_MMM_D_Y_FORMATS } from 'core/date/material-datefns-date-adapter';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { isNilOrEmpty } from 'helpers';

export type FollowUpFlagActions = 'CreateFlag' | 'EditFlag';

/**
 * Format of data for the dialog
 */
export interface FollowUpFlagDialogData {
  campaignId: string;
  entity: EntityWithBasicInfoFragment & EntityCoreFragment;
}

@Component({
  selector: 'app-follow-up-flag-dialog',
  templateUrl: './follow-up-flag.dialog.html',
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_MMM_D_Y_FORMATS }
  ]
})
export class FollowUpFlagDialog extends WithDisposableSubject()
  implements OnInit {
  /* will probably be useful later */
  isSaving = false;
  noteControl = new UntypedFormControl({ value: '', disabled: false }, { validators: [Validators.required] });
  searchControl = new UntypedFormControl();
  dueDateControl = new UntypedFormControl();

  filteredResults$ = this.searchControl.valueChanges.pipe(
    debounceTime(500),
    switchMap((value) => {
      if (isNilOrEmpty(value) || typeof (value) !== 'string') {
        return of([]);
      }

      return this._store.select(selectCampaignId).pipe(take(1))
        .pipe(
          switchMap((campaignId) => this._searchUsersGQL.fetch({ keyword: value, campaignId }, {
            fetchPolicy: 'network-only'
          }).pipe(
            map(({ data }) => data.searchUsers.edges.map(n => n.node)),
            map((results) => {
              if (isNilOrEmpty(results)) {
                return ['NO_RESULTS'];
              }
              return results;
            }),
            catchError((_) => of([])),
          ))
        );
    })
  );

  formGroup = new UntypedFormGroup({
    noteControl: this.noteControl
  });

  static open(dialog: MatDialog, config: MatDialogConfig<FollowUpFlagDialogData>) {
    return dialog.open(FollowUpFlagDialog, config);
  }

  constructor(
    private _store: Store,
    public _dialogRef: MatDialogRef<FollowUpFlagDialog>,
    private _cd: ChangeDetectorRef,
    private _createFollowUpGQL: CreateFollowUpGQL,
    private _searchUsersGQL: SearchUserQueryGQL,
    @Inject(MAT_DIALOG_DATA) public data: FollowUpFlagDialogData
  ) {
    super();
  }

  ngOnInit(): void {
    if (isNilOrEmpty(this.data?.entity)) {
      throw new Error('An entity must be passed as data to `FollowUpFlagDialog`');
    }

    // Default the search box to the current user
    this._store.select(
      selectCurrentUser
    ).pipe(
      takeUntil(this.disposer)
    ).subscribe(
      (currentUser) => this.searchControl.setValue(currentUser)
    );
  }

  userDisplayFn(user) {
    if (!user) { return ''; }
    return `${user.firstName} ${user.lastName}`;
  }

  onSubmit() {
    if (this.formGroup.invalid) {
      return;
    }

    if (this.isSaving) { return; }
    this.isSaving = true;

    this._createFollowUpFlag();
  }

  private _createFollowUpFlag() {
    this._createFollowUp().pipe(
      finalize(() => {
        this.isSaving = false;
        this._cd.markForCheck();
      })
    ).subscribe((entity) => {
      this.close(entity);
    });
  }

  close(data: EntityWithBasicInfoFragment & EntityCoreFragment = null) {
    this._dialogRef.close(data);
  }

  private _createFollowUp() {
    const entity = this.data.entity;
    return this._createFollowUpGQL.mutate({
      input: {
        campaignId: entity.campaignContextId,
        entityId: entity.id,
        note: this.noteControl.value,
        assignedUserId: this.searchControl.value.id,
        dueDate: this.dueDateControl.value ? format(this.dueDateControl.value, 'yyyy-MM-dd') : undefined
      }
    }).pipe(
      map(d => d.data.createFollowUp.entity)
    );
  }

}
